<template>
  <div class="profileSidebar">
    <template
      v-for="link in navLinks"
      :key="link.url"
    >
      <NuxtLink
        class="profileSidebar-link"
        :to="link.url"
        @click.capture="onShowContent(link)"
      >
        <span class="profileSidebar-link__content">
          <span>{{ link.title }}</span>
          <span
            v-if="link.slotName === 'orders' && unreadCount > 0"
            class="profileSidebar-link__badge"
          >{{ displayUnreadCount }}</span>
        </span>
        <img
          src="~/assets/img/svg/profile/arrow.svg"
          alt="arrow"
        >
      </NuxtLink>
      <template v-if="isShowContent">
        <slot
          class="profile-sidebar__wrap"
          :name="link.slotName"
        ></slot>
      </template>
    </template>
    <client-only>
      <div
        v-if="isLoggedIn"
        class="btn btn-primary logout-btn"
        @click="onLogout"
      >
        Выход
      </div>
    </client-only>
    <span
      v-if="isLoggedIn"
      class="profile-sidebar__remove-acc"
      @click="isOpenConfirmModal = true"
    >
      Удалить аккаунт
    </span>
    <VCupertino
      v-if="isOpenConfirmModal"
      id="remove-user"
      class="user-editor"
      :drawer-options="{
        modal:true,
        cssClass:'profile-sidebar__remove-user-modal',
      }"
      :is-show="isOpenConfirmModal"

      @backdropTap="beforeDestroy"
      @didDismiss="beforeDestroy"
    >
      <RemoveUserPopup
        :current-user="currentUser"
        @closeModal="beforeDestroy"
      />
    </VCupertino>
  </div>
</template>

<script setup lang="ts">
import { navigateTo } from '#imports';
import RemoveUserPopup from "~/components/profile/user/RemoveUserPopup.vue";

const VCupertino = defineAsyncComponent(() => import('~/components/widget/VCupertino.vue'));

const $route = useRoute();

type NavLinkType = {
	title: string;
	url: string;
	slotName: string;
}

const {
  logOut,
  isLoggedIn,
  currentUser,
  getUser
} = useSitisAuth();

const navLinks: ComputedRef<NavLinkType[]> = computed( () => {
  if (isLoggedIn.value) {
    return [
      {title: 'Мои данные', url: '/profile', slotName: 'profile'},
      {title: 'Мои заказы', url: '/profile/orders', slotName: 'orders'},
      {title: 'Избранные товары', url: '/wishlist', slotName: 'wish'},
      {title: 'Недавно смотрели', url: '/recently', slotName: 'recently'}
    ]
  } else {
    return [
      {title: 'Избранные товары', url: '/wishlist', slotName: 'wish'},
      {title: 'Недавно смотрели', url: '/recently', slotName: 'recently'}
    ]
  }
});

const isShowContent = ref<boolean>(true);
const { unreadCount, displayUnreadCount } = useCustomerNotificationsBadge();

const onLogout = () => {
  logOut();
  navigateTo('/');
};

function onShowContent (link: NavLinkType) {
  if ($route.path === link.url) {
    isShowContent.value = !isShowContent.value;
  }
}

const isOpenConfirmModal = ref(false);
const beforeDestroy = () => {
  isOpenConfirmModal.value = false;
};
</script>

<style scoped lang="scss">
.profileSidebar {
	max-width: 261px;
	width: 100%;
	border-right: 1px solid rgb(0 0 0 / 10%);
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 16px;
}

.profileSidebar-link {
	font-size: 12px;
	font-weight: 500;
	padding: 13px 24px;
	border-radius: 16px;
	border: 1px solid #E2E2E2;
	display: flex;
	justify-content: center;
	align-items: center;
	max-height: 60px;
	min-height: 60px;
	height: 100%;

	& > img {
		display: none;
	}

	&.router-link-active {
		background: #EBEBEB;
	}
}

.profileSidebar-link__content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.profileSidebar-link__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 100px;
  background: #ED908C;
  color: #FFF;
  font-size: 10px;
  line-height: 1;
}

.logout-btn {
	max-width: 133px;
	width: 100%;
	text-align: center;
	height: 60px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 16px;
	margin-top: 64px;
}

.profile-sidebar__remove-acc {
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  cursor: pointer;
  color: var(--red);
  &:hover {
    text-decoration: underline;
  }
}

.profileSidebar {
  &:deep(.profile-sidebar__remove-user-modal) {
    z-index: 100;

    & .h2 {
      margin-bottom: 16px;
    }

    & .h3 {
      text-align: center;
    }

    & .pane {
      max-width: 550px;
      width: 100%;
      padding: 30px;
    }
  }
}

@media (max-width: 1024px) {
	.profileSidebar {
		max-width: 100%;
		border-right: none;
	}
	.profileSidebar-link {
		width: 100%;
		max-height: 48px;
		min-height: 48px;
		border-radius: 12px;
		border-color: transparent;
		justify-content: space-between;
		font-size: 16px;
		font-style: normal;
		font-weight: 600;
		line-height: 140%;
		background: var(--gray);

		& > img {
			display: initial;
		}

		&.router-link-active {
			background: var(--gray);
		}
	}

  .profileSidebar {
    &:deep(.profile-sidebar__remove-user-modal) {
      & .pane {
        padding: 16px;
      }
    }
  }
}
</style>
